import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

export default function AdvisorLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userData = await login(email, password);
      toast.success('Welcome back!');
      const returnTo = location.state?.returnTo;
      // Only our protected submission route is eligible; never redirect to a supplied external URL.
      if (typeof returnTo === 'string' && /^\/advisor\/submission\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(returnTo)) {
        navigate(returnTo, { replace: true });
      } else if (userData?.role === 'admin') {
        navigate('/admin/dashboard');
      } else if (userData?.role === 'csa') {
        navigate('/csa/dashboard');
      } else {
        navigate('/advisor/dashboard');
      }
    } catch (e) {
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 36, 68, 0.8), rgba(30, 58, 99, 0.8)), url('https://images.pexels.com/photos/5816285/pexels-photo-5816285.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-center items-center text-white p-12">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.05em' }}>
            <span style={{ fontWeight: 800 }}>STEELPEAK</span>{' '}
            <span style={{ fontWeight: 300 }}>WEALTH</span>
          </h1>
          <p className="text-xl text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Trusted wealth management for your future
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#f8fafc]">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="login-heading">
                Advisor Login
              </h2>
              <p className="text-[#475569]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Access your client dashboard
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md" data-testid="login-error">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-[#0f172a]">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@spwm.com"
                  autoComplete="email"
                  className="mt-2 bg-white border-[#e2e8f0] focus:ring-[#5b93d6] focus:border-[#5b93d6]"
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-[#0f172a]">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="mt-2 bg-white border-[#e2e8f0] focus:ring-[#5b93d6] focus:border-[#5b93d6]"
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] hover:opacity-90 text-white font-medium py-2.5 transition-all hover:shadow-md"
                data-testid="login-submit-button"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <Link
                to="/advisor/forgot-password"
                className="block text-sm text-[#5b93d6] hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </Link>
              <div className="text-sm text-[#475569]">
                New advisor?{' '}
                <Link to="/advisor/signup" className="text-[#5b93d6] hover:underline" data-testid="signup-link">
                  Create an account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
